import React, { useState } from 'react'

import { employersEmployerSettings } from 'rails_routes'
import { type PayrollConfig } from 'types/payroll'
import { type PayrollProviderConfig } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import PayrollConnectionPicker from './payroll/PayrollConnectionPicker'
import PayrollSyncStatus from './PayrollSyncStatus'
import PayScheduleStatus from './PayScheduleStatus'

interface PayrollSettingsProps {
  payrollConfig: PayrollConfig | null
  disablePayrollSync: boolean,
  payrollProviders?: PayrollProviderConfig[]
  promotedPayrollProviders?: PayrollProviderConfig[]
  customScheduleCount?: number
  unverifiedScheduleCount?: number
}

const PayrollSettings: React.FC<PayrollSettingsProps> = ({
  payrollConfig,
  disablePayrollSync,
  payrollProviders = [],
  promotedPayrollProviders = [],
  customScheduleCount = 0,
  unverifiedScheduleCount = 0,
}) => {
  const { assign } = useWindowLocation()

  const [hasPayrollIntegration, setHasPayrollIntegration] = useState(
    payrollConfig?.has_payroll_integration ?? false
  )

  const lastEmployeeSync = payrollConfig?.last_employee_sync ? new Date(payrollConfig.last_employee_sync) : null
  const nextEmployeeSync = payrollConfig?.next_employee_sync ? new Date(payrollConfig.next_employee_sync) : null

  const handleDisconnectSuccess = () => {
    setHasPayrollIntegration(false)
  }

  return (
    <div>
      <div>
        <h2 className='heading-md'>Pay schedules</h2>
        <div className='pt-1 mb-12'>
          <p className='max-w-2xl body-sm text-gray-500 mb-4'>
            Manage your pay schedules to ensure your employees' deductions are calculated based on the correct pay dates.
          </p>
          <PayScheduleStatus
            customScheduleCount={customScheduleCount}
            unverifiedScheduleCount={unverifiedScheduleCount}
          />
        </div>

        {!disablePayrollSync && (
          <>
            <h2 className='h2 mb-4'>Payroll connection</h2>

            {hasPayrollIntegration && payrollConfig
              ? (
                <PayrollSyncStatus
                  isConnectedToPayroll={payrollConfig.connected_to_payroll}
                  lastEmployeeSync={lastEmployeeSync}
                  nextEmployeeSync={nextEmployeeSync}
                  payrollIntegrationProvider={payrollConfig.provider_display_name}
                  providerIconUrl={payrollConfig.provider_icon_url}
                  providerApiConnectionMethod={payrollConfig.provider_api_connection_method}
                  providerConfigId={payrollConfig.provider_config_id}
                  supportsAutomaticDeductions={payrollConfig.supports_automatic_deductions}
                  automaticDeductionsSetupComplete={payrollConfig.automatic_deductions_setup_complete}
                  automaticDeductionsEnabled={payrollConfig.automatic_deductions_enabled}
                  onDisconnectSuccess={handleDisconnectSuccess}
                  requiresEmployeeDeduplication={payrollConfig.requires_employee_deduplication}
                  hasInvitedManuallyAddedEmployees={payrollConfig.has_invited_manually_added_employees}
                  showAutomatedDeductionsNotice={payrollConfig.show_automated_deductions_notice}
                  hidePayrollManagement={payrollConfig.hide_payroll_management}
                />
                )
              : (
            /* If no payroll integration, show the connection picker */
                <>
                  <p className='max-w-2xl text-sm text-gray-500'>
                    Connect your payroll provider to sync your employee roster and enable payroll deductions.
                  </p>
                  <div className='mt-4'>
                    <PayrollConnectionPicker
                      payrollProviders={payrollProviders}
                      promotedPayrollProviders={promotedPayrollProviders}
                      onSuccessCallback={() => {
                        assign(employersEmployerSettings.show({ query: { tab: 'payroll' } }))
                      }}
                      onSuccessUrl={employersEmployerSettings.show({ query: { tab: 'payroll' } })}
                    />
                  </div>
                </>
                )}
          </>
        )}
      </div>
    </div>
  )
}

export default PayrollSettings
